import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { apiService, API_ENDPOINTS } from '../config/api';
import { SupportService } from '../services/supportService';
import { SUPPORT_CATEGORIES, SUPPORT_IMAGE_TYPES, SUPPORT_IMAGE_LIMIT, SUPPORT_IMAGE_MAX_BYTES, validParticipantCount } from '../config/support';
import type {
  SupportFormData,
  SupportScreenshot,
  SupportRequestPayload,
  SupportTicketReceipt,
} from '../types';

const DRAFT_KEY = 'studysync_support_draft';

const INITIAL_FORM_DATA: SupportFormData = {
  category: null,
  description: '',
  urgency: false,
  browserDevice: '',
  studyId: '',
  studyName: '',
  participantsAffected: '',
  errorMessage: '',
  participantIdentifier: '',
  enrollmentStep: '',
  additionalNotes: '',
  screenshots: [],
};

function parseBrowserInfo(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';

  if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';

  return `${browser} on ${os}`;
}

function compressImage(file: File, maxWidth = 1200, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl.split(',')[1]);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export type SubmitStatus = 'idle' | 'success' | 'error';

export interface UseSupportFormReturn {
  formData: SupportFormData;
  setField: <K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => void;
  resetCategory: () => void;
  isSubmitting: boolean;
  submitStatus: SubmitStatus;
  error: string | null;
  ticket: SupportTicketReceipt | null;
  submitForm: () => Promise<void>;
  hasDraft: boolean;
  restoreDraft: () => void;
  dismissDraft: () => void;
  resetForm: () => void;
  detectedStudyId: string;
  detectedStudyName: string;
  addScreenshots: (files: File[]) => void;
  removeScreenshot: (id: string) => void;
  browserInfo: string;
}

export function useSupportForm(): UseSupportFormReturn {
  const { user } = useSecureAuth();
  const location = useLocation();

  const [formData, setFormData] = useState<SupportFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<SupportTicketReceipt | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [detectedStudyId, setDetectedStudyId] = useState('');
  const [detectedStudyName, setDetectedStudyName] = useState('');
  const [browserInfo] = useState(() => parseBrowserInfo());

  const draftKey = user?.uid ? `${DRAFT_KEY}:${user.uid}` : null;
  const requestKey = draftKey ? `${draftKey}:submission` : null;
  const lastSubmission = useRef<{ fingerprint: string; requestId: string } | null>(null);
  const submittingRef = useRef(false);
  const screenshotsRef = useRef(formData.screenshots);
  screenshotsRef.current = formData.screenshots;
  const clearDraft = useCallback(() => {
    try { if (draftKey) sessionStorage.removeItem(draftKey); } catch { /* Storage can be disabled. */ }
  }, [draftKey]);
  const draftCheckedRef = useRef(false);

  // Ignore late responses when the user changes studies while support is open.
  useEffect(() => {
    let active = true;
    const id = location.pathname.match(/\/researcher\/study\/([^/]+)/)?.[1] || '';
    setDetectedStudyId(id);
    setDetectedStudyName('');
    setFormData(prev => ({ ...prev, studyId: id, studyName: '' }));
    if (id && user) apiService.get(API_ENDPOINTS.STUDIES.GET(id)).then((res: any) => {
      if (!active) return;
      const name = res?.study?.name || res?.name || res?.data?.name || '';
      setDetectedStudyName(name);
      setFormData(prev => prev.studyId === id ? { ...prev, studyName: name } : prev);
    }).catch(() => {});
    return () => { active = false; };
  }, [location.pathname, user]);

  // Check for saved draft on mount
  useEffect(() => {
    if (draftCheckedRef.current) return;
    draftCheckedRef.current = true;

    try {
      const saved = (draftKey ? sessionStorage.getItem(draftKey) : null);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.category || parsed.description) {
          setHasDraft(true);
        }
      }
    } catch {
      // ignore
    }
  }, [draftKey]);

  // Small text-only drafts are private to the signed-in account in this tab.
  const saveDraft = useCallback((data: SupportFormData) => {
    if (!draftKey) return;
    try {
      const { screenshots, ...saveable } = data;
      sessionStorage.setItem(draftKey, JSON.stringify(saveable));
    } catch { /* Storage can be disabled. */ }
  }, [draftKey]);

  const setField = useCallback(<K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => {
    if (submittingRef.current) return;
    setError(null);
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const resetCategory = useCallback(() => {
    if (submittingRef.current) return;
    setFormData((prev) => ({
      ...prev,
      category: null,
    }));
    setSubmitStatus('idle');
    setError(null);
  }, []);

  const restoreDraft = useCallback(() => {
    try {
      const saved = (draftKey ? sessionStorage.getItem(draftKey) : null);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.category && !Object.prototype.hasOwnProperty.call(SUPPORT_CATEGORIES, parsed.category)) return;
        const restored = Object.fromEntries(Object.entries(INITIAL_FORM_DATA)
          .filter(([key, value]) => key !== 'screenshots' && typeof parsed[key] === typeof value)
          .map(([key]) => [key, parsed[key]]));
        setFormData((prev) => ({
          ...prev,
          ...restored,
          category: parsed.category || null,
          screenshots: prev.screenshots, // keep current screenshots
          // Update auto-detected study if on a study page
          studyId: detectedStudyId || parsed.studyId || '',
          studyName: detectedStudyName || parsed.studyName || '',
        }));
      }
    } catch {
      // ignore
    }
    setHasDraft(false);
  }, [detectedStudyId, detectedStudyName, draftKey]);

  const dismissDraft = useCallback(() => {
    clearDraft();
    setHasDraft(false);
  }, [clearDraft]);

  const resetForm = useCallback(() => {
    if (submittingRef.current) return;
    screenshotsRef.current.forEach(s => URL.revokeObjectURL(s.preview));
    setFormData({
      ...INITIAL_FORM_DATA,
      studyId: detectedStudyId,
      studyName: detectedStudyName,
    });
    setSubmitStatus('idle');
    setError(null);
    setIsSubmitting(false);
    setTicket(null);
    lastSubmission.current = null;
    try { if (requestKey) sessionStorage.removeItem(requestKey); } catch { /* Storage can be disabled. */ }
    clearDraft();
    setHasDraft(false);
  }, [detectedStudyId, detectedStudyName, clearDraft, requestKey]);

  const addScreenshots = useCallback((files: File[]) => {
    if (submittingRef.current || !files.length) return;
    if (files.length + screenshotsRef.current.length > SUPPORT_IMAGE_LIMIT) {
      setError('You can attach up to 3 screenshots. Remove one before adding another.');
      return;
    }
    if (files.some(file => !SUPPORT_IMAGE_TYPES.includes(file.type) || file.size > SUPPORT_IMAGE_MAX_BYTES)) {
      setError('Choose JPG, PNG, GIF or WebP images up to 5 MB each.');
      setSubmitStatus('error');
      return;
    }
    setError(null);
    const added: SupportScreenshot[] = files.map(file => ({ id: crypto.randomUUID(), file, preview: URL.createObjectURL(file) }));
    screenshotsRef.current = [...screenshotsRef.current, ...added];
    const screenshots = screenshotsRef.current;
    setFormData(prev => ({ ...prev, screenshots }));
  }, []);

  const removeScreenshot = useCallback((id: string) => {
    if (submittingRef.current) return;
    const removed = screenshotsRef.current.find(s => s.id === id);
    if (removed) URL.revokeObjectURL(removed.preview);
    const screenshots = screenshotsRef.current.filter(s => s.id !== id);
    screenshotsRef.current = screenshots;
    setFormData(prev => ({ ...prev, screenshots }));
    setError(null);
  }, []);

  const submitForm = useCallback(async () => {
    if (submittingRef.current || ticket) return;
    if (!formData.category || formData.description.trim().length < 10 || !user?.email) {
      setError('Sign in and describe the problem in at least 10 characters.');
      setSubmitStatus('error');
      return;
    }
    const fields = SUPPORT_CATEGORIES[formData.category].fields;
    if (fields.includes('participants') && !validParticipantCount(formData.participantsAffected)) {
      setError('Enter a whole number of participants, 0 or more.');
      setSubmitStatus('error');
      return;
    }
    submittingRef.current = true;

    setIsSubmitting(true);
    setError(null);
    setSubmitStatus('idle');

    try {
      // Compress screenshots
      const compressedScreenshots = await Promise.all(
        formData.screenshots.map(async (s) => {
          const data = s.compressed || await compressImage(s.file);
          if (data.length > 3000000) throw new Error('A screenshot is too large. Please remove it or choose a smaller image.');
          return { filename: 'screenshot.jpg', data, mimeType: 'image/jpeg' as const };
        })
      );

      const payload: SupportRequestPayload = {
        category: formData.category,
        description: formData.description.trim(),
        urgency: formData.urgency,
        browserInfo,
      };

      if (formData.browserDevice) payload.browserDevice = formData.browserDevice;
      if (fields.includes('study') && formData.studyId) {
        payload.studyId = formData.studyId;
        if (formData.studyName) payload.studyName = formData.studyName;
      }
      if (fields.includes('participants') && formData.participantsAffected) payload.participantsAffected = Number(formData.participantsAffected);
      if (formData.errorMessage) payload.errorMessage = formData.errorMessage;
      if (fields.includes('participant') && formData.participantIdentifier) payload.participantIdentifier = formData.participantIdentifier;
      if (fields.includes('enrollment') && formData.enrollmentStep) payload.enrollmentStep = formData.enrollmentStep;
      if (formData.additionalNotes) payload.additionalNotes = formData.additionalNotes;

      if (compressedScreenshots.length > 0) payload.screenshots = compressedScreenshots;

      // Retain an opaque request ID across network retries, including reopening
      // the panel. Store only a hash here, never the screenshot data.
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)));
      const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      if (!lastSubmission.current && requestKey) {
        try { lastSubmission.current = JSON.parse(sessionStorage.getItem(requestKey) || 'null'); } catch { /* Storage can be disabled. */ }
      }
      if (lastSubmission.current?.fingerprint !== fingerprint) lastSubmission.current = { fingerprint, requestId: crypto.randomUUID() };
      payload.requestId = lastSubmission.current!.requestId;
      try { if (requestKey) sessionStorage.setItem(requestKey, JSON.stringify(lastSubmission.current)); } catch { /* Storage can be disabled. */ }
      const response = await SupportService.submitSupportRequest(payload);
      setTicket(response.ticket);

      setSubmitStatus('success');
      clearDraft();
      try { if (requestKey) sessionStorage.removeItem(requestKey); } catch { /* Storage can be disabled. */ }
      setHasDraft(false);
    } catch (err: any) {
      setSubmitStatus('error');
      const message = err.message || 'We could not confirm your ticket was saved. Please retry this request.';
      setError(err.requestId ? `${message} Reference: ${err.requestId}` : message);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [formData, user, browserInfo, clearDraft, ticket, requestKey]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      screenshotsRef.current.forEach((s) => URL.revokeObjectURL(s.preview));
    };
  }, []);

  return {
    formData,
    setField,
    resetCategory,
    isSubmitting,
    submitStatus,
    error,
    ticket,
    submitForm,
    hasDraft,
    restoreDraft,
    dismissDraft,
    resetForm,
    detectedStudyId,
    detectedStudyName,
    addScreenshots,
    removeScreenshot,
    browserInfo,
  };
}
